import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { getCabinetMe, getYearSummary } from "../api/cabinet";
import { api } from "../api/client";
import { useState } from "react";

/**
 * «Сводка года» (D4) — светлая премиум-ретроспектива достижений ребёнка.
 *
 * Три состояния (invitation_status):
 *   invited     → «золотой билет» + (как всем) сертификат летописца;
 *   not_invited → достойный год + «школа видит и ценит» + сертификат летописца;
 *   pending     → нейтрально: «решение весной, летопись пишется».
 *
 * Внутреннюю кухню отбора семья не видит — только тёплый итог (ФЗ-152/этика).
 */

const SPHERE_META: Record<
  string,
  { label: string; icon: string; bar: string }
> = {
  diploma: { label: "Дипломы и олимпиады", icon: "🏆", bar: "linear-gradient(90deg,#b08327,#caa24a)" },
  sport: { label: "Спорт", icon: "⚽", bar: "linear-gradient(90deg,#0f8a5f,#34c08a)" },
  project: { label: "Проекты", icon: "🎯", bar: "linear-gradient(90deg,#2257d6,#5b8af0)" },
  initiative: { label: "Инициатива и добро", icon: "✨", bar: "linear-gradient(90deg,#6b4bd6,#9a7df0)" },
  gratitude: { label: "Благодарности", icon: "💝", bar: "linear-gradient(90deg,#c2476b,#e07a98)" },
};

export function YearSummaryPage() {
  const [params] = useSearchParams();
  const paramStudent = params.get("student_id");

  const { data: me } = useQuery({ queryKey: ["cabinet-me"], queryFn: getCabinetMe });
  const studentId = paramStudent
    ? parseInt(paramStudent, 10)
    : me?.children[0]?.id ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ["year-summary", studentId],
    queryFn: () => getYearSummary(studentId!),
    enabled: !!studentId,
  });

  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState("");

  async function downloadCertificate() {
    if (!data) return;
    setDlError("");
    setDownloading(true);
    try {
      const res = await api.get("/cabinet/letopisec-certificate", {
        params: { student_id: data.student_id, year_id: data.year_id },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `Сертификат_летописца_${data.year_name.replace("/", "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDlError("Не удалось сформировать сертификат. Попробуйте позже.");
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading || (!data && !error)) {
    return (
      <div className="min-h-screen bg-[var(--color-warm)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Собираем итоги года…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--color-warm)]">
        <CabinetHeader />
        <main className="max-w-3xl mx-auto px-6 py-10 text-center">
          <p className="text-[var(--color-cobalt)] mb-4">Не удалось загрузить сводку года.</p>
          <Link to="/cabinet" className="underline">← В кабинет</Link>
        </main>
      </div>
    );
  }

  const maxCount = Math.max(1, ...data.spheres.map((s) => s.count));
  const serif = { fontFamily: "var(--font-serif)" };

  return (
    <div className="min-h-screen bg-[var(--color-warm)] relative overflow-hidden">
      {/* тёплые сияния по углам */}
      <div
        className="pointer-events-none absolute -top-48 -right-32 w-[560px] h-[560px] rounded-full"
        style={{ background: "radial-gradient(circle,rgba(202,162,74,.22),transparent 62%)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-56 -left-40 w-[580px] h-[580px] rounded-full"
        style={{ background: "radial-gradient(circle,rgba(0,51,255,.07),transparent 60%)" }}
      />
      <div className="relative">
        <CabinetHeader displayName={me?.parent.display_name} />
        <main className="max-w-3xl mx-auto px-6 py-10 pb-24">
          <Link to="/cabinet" className="text-sm text-[var(--color-muted)] hover:text-[var(--color-cobalt)] mb-6 inline-block">
            ← В кабинет
          </Link>

          <div className="italic text-[var(--color-gold)] text-xl" style={serif}>
            Летопись · {data.year_name} · Том I
          </div>
          <h1 className="font-[200] tracking-[-0.04em] leading-[0.92] text-5xl sm:text-7xl mt-1">
            Итоги года
          </h1>
          <div className="mt-3 text-lg" style={serif}>
            {data.student_name}
            {data.student_class && (
              <span className="text-[var(--color-muted)]"> · {data.student_class} класс</span>
            )}
          </div>

          {/* Крупная цифра */}
          <div className="flex items-baseline gap-6 flex-wrap mt-12 mb-2">
            <div
              className="font-[200] leading-[0.85] tracking-[-0.05em] text-[88px] sm:text-[150px]"
              style={{
                background: "linear-gradient(160deg,var(--color-cobalt),var(--color-gold))",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {data.approved_count}
            </div>
            <div className="text-2xl max-w-[280px]" style={serif}>
              {data.approved_count === 0
                ? "год только начинается — впереди целая летопись"
                : `${pluralAch(data.approved_count)} в летописи за год`}
            </div>
          </div>

          {/* Сферы */}
          {data.spheres.length > 0 && (
            <div className="grid gap-3 max-w-2xl mt-8">
              {data.spheres.map((s) => {
                const m = SPHERE_META[s.type] ?? { label: s.type, icon: "•", bar: "var(--color-cobalt)" };
                return (
                  <div key={s.type} className="grid grid-cols-[180px_1fr_40px] items-center gap-3.5">
                    <div className="text-[13px] font-bold tracking-[0.04em] uppercase text-[var(--color-muted)]">
                      {m.icon} {m.label}
                    </div>
                    <div className="h-3 rounded-full bg-black/[0.07] overflow-hidden">
                      <div className="h-full rounded-full transition-[width] duration-700"
                        style={{ width: `${(s.count / maxCount) * 100}%`, background: m.bar }} />
                    </div>
                    <div className="font-bold text-right">{s.count}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Кульминация по статусу ─────────────────────────────── */}
          <div className="mt-14">
            {data.invitation_status === "invited" ? (
              <Ticket data={data} />
            ) : data.invitation_status === "not_invited" ? (
              <WorthyYear name={data.student_name} />
            ) : (
              <Pending reception={data.reception_label} />
            )}
          </div>

          {/* ── Сертификат летописца (всем, кто вёл летопись) ──────── */}
          {data.is_letopisec && (
            <div className="mt-8 rounded-2xl border border-[var(--color-line)] bg-white/70 p-6">
              <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-[var(--color-gold)] mb-1">
                Признание школы
              </div>
              <h3 className="text-xl mb-1" style={serif}>Сертификат летописца</h3>
              <p className="text-sm text-[var(--color-muted)] mb-4 max-w-lg">
                Именной документ за то, что семья весь год вела летопись достижений
                ребёнка. Можно скачать и сохранить на память.
              </p>
              <Button variant="primary" onClick={downloadCertificate} loading={downloading}>
                📜 Скачать сертификат
              </Button>
              {dlError && <p className="text-sm text-[var(--color-red)] mt-2">{dlError}</p>}
            </div>
          )}

          <p className="mt-9 text-[12.5px] text-[var(--color-muted)]">
            Это приватная страница вашей семьи. Поделиться наружу нельзя — только скачать на память.
          </p>
        </main>
      </div>
    </div>
  );
}

// ── Состояние: приглашён → «золотой билет» ───────────────────────────
function Ticket({ data }: { data: { student_name: string; reception_label: string; nomination: { title: string; icon: string | null } | null } }) {
  return (
    <div
      className="relative overflow-hidden rounded-[22px] p-8 border"
      style={{
        background: "linear-gradient(135deg,#fffdf6,#f4ead0)",
        borderColor: "var(--color-gold-2)",
        boxShadow: "0 10px 34px -16px rgba(20,16,8,.30)",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(120% 160% at 88% -10%,rgba(202,162,74,.30),transparent 55%)" }}
      />
      <div className="absolute top-6 right-6 w-[66px] h-[66px] rounded-full grid place-items-center text-center text-[9px] font-extrabold tracking-[0.12em] leading-tight"
        style={{ border: "1.5px solid var(--color-gold)", color: "var(--color-gold)", background: "rgba(255,255,255,.55)" }}>
        ПРИЁМ
      </div>
      <div className="relative">
        <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[var(--color-gold)]">
          ✦ Личное приглашение директора
        </div>
        <h3 className="text-3xl sm:text-[42px] leading-[1.05] my-3" style={{ fontFamily: "var(--font-serif)" }}>
          Приглашение на торжественный приём «Гордость 94»
        </h3>
        <p className="text-[#6b6356] max-w-lg">
          {data.student_name}{data.nomination ? ` — ${data.nomination.icon ?? ""} ${data.nomination.title}` : ""}.
          {" "}{data.reception_label}, актовый зал гимназии. Среди тех, чьим путём гимназия гордится в этом году.
        </p>
      </div>
    </div>
  );
}

// ── Состояние: не приглашён, но год достойный ───────────────────────
function WorthyYear({ name }: { name: string }) {
  return (
    <div className="rounded-[22px] border border-[var(--color-line)] bg-white/70 p-8">
      <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[var(--color-cobalt)]">
        Достойный год
      </div>
      <h3 className="text-3xl leading-[1.1] my-3" style={{ fontFamily: "var(--font-serif)" }}>
        Школа видит и ценит этот год
      </h3>
      <p className="text-[var(--color-muted)] max-w-lg">
        Каждое достижение {name} — повод для гордости. Летопись продолжается:
        всё, что вы собрали в этом году, остаётся в портфолио навсегда, а впереди —
        новый Том.
      </p>
    </div>
  );
}

// ── Состояние: решение ещё не принято ────────────────────────────────
function Pending({ reception }: { reception: string }) {
  return (
    <div className="rounded-[22px] border border-[var(--color-line)] bg-white/70 p-8">
      <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-[var(--color-muted)]">
        Летопись пишется
      </div>
      <h3 className="text-3xl leading-[1.1] my-3" style={{ fontFamily: "var(--font-serif)" }}>
        Год продолжается
      </h3>
      <p className="text-[var(--color-muted)] max-w-lg">
        Решение о приглашении на приём директор примет весной ({reception}).
        Продолжайте летопись — каждое достижение делает её ярче.
      </p>
    </div>
  );
}

function pluralAch(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return `${n} достижение`;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return `${n} достижения`;
  return `${n} достижений`;
}
